/**
 * Pure validation functions shared between usePropertyForm and useEditPropertyForm.
 * Each function returns { ok: true } or { ok: false, field: string, message: string }.
 * No side effects — callers decide how to display errors.
 */

// ── Regex ──────────────────────────────────────────────────────────────────────

export const YOUTUBE_RE = /^(https?:\/\/)?(www\.)?(m\.)?((youtube\.com)|(youtu\.be))\/.+$/;
export const VR_RES = [
  /^https:\/\/(my\.matterport\.com\/show\/\?.*m=.+|matterport\.com\/discover\/space\/.+)$/,
  /^https?:\/\/[a-zA-Z0-9_-]+\.cloudpano\.com\/tours\/[a-zA-Z0-9_-]+\/?$/,
  /^https?:\/\/kuula\.co\/[a-zA-Z0-9_-]+\/?$/,
  /^https?:\/\/[a-zA-Z0-9_-]+\.pano360\.com\/[a-zA-Z0-9_-]+\/?$/,
];
export const PHONE_RE = /^0\d{9}$/;

export const isValidYouTubeUrl = (url) => YOUTUBE_RE.test(url);
export const isValidVrUrl      = (url) => VR_RES.some((re) => re.test(url));

// ── Helpers ────────────────────────────────────────────────────────────────────

export function parsePrice(val) {
  if (val === null || val === undefined || val === '') return 0;
  return parseFloat(String(val).replace(/,/g, '')) || 0;
}

const ok  = () => ({ ok: true });
const err = (field, message) => ({ ok: false, field, message });

// ── Validators ─────────────────────────────────────────────────────────────────

export function validatePage1(upload) {
  const { images, youtube_url, vr_url } = upload;

  if (images.length === 0)
    return err('images', 'กรุณาใส่รูปภาพอย่างน้อย 1 รูป');
  if (youtube_url && !isValidYouTubeUrl(youtube_url))
    return err('youtube_url', 'ลิงก์ YouTube ไม่ถูกต้อง');
  if (vr_url && !isValidVrUrl(vr_url))
    return err('vr_url', 'VR URL รองรับ Matterport, Cloudpano, Kuula, Pano360');

  return ok();
}

export function validatePage2(upload, config) {
  const {
    heading_post, detail_post, type_post,
    floor, area, unit_area, use_area, use_unit_area,
    bedroom, bathroom,
    rai, ngan, wah,
    price, rent_price, sellout_price,
  } = upload;

  if (!heading_post?.trim())
    return err('heading_post', 'กรุณาระบุหัวข้อประกาศ');
  if (!detail_post?.trim())
    return err('detail_post', 'กรุณาระบุรายละเอียด');
  if (type_post.length === 0)
    return err('type_post', 'กรุณาเลือกประเภทโพสต์ (ขาย / เช่า / เซ้ง)');

  if (config.hasRooms) {
    if (!floor || Number(floor) <= 0 || Number(floor) > 200)
      return err('floor', 'กรุณาระบุจำนวนชั้น (1–200)');
    if (bedroom === null || bedroom === '')
      return err('bedroom', 'กรุณาระบุจำนวนห้องนอน');
    if (bathroom === null || bathroom === '')
      return err('bathroom', 'กรุณาระบุจำนวนห้องน้ำ');
  }

  if (config.hasArea) {
    if (!area || Number(area) <= 0)
      return err('area', 'กรุณาระบุขนาดพื้นที่ให้ถูกต้อง');
    if (!unit_area)
      return err('unit_area', 'กรุณาเลือกหน่วยพื้นที่ (ตร.ม. / ตร.ว.)');
  }

  if (config.hasUseArea) {
    if (!use_area || Number(use_area) <= 0)
      return err('use_area', 'กรุณาระบุพื้นที่ใช้สอย');
    if (!use_unit_area)
      return err('use_unit_area', 'กรุณาเลือกหน่วยพื้นที่ใช้สอย');
  }

  if (config.hasLandArea) {
    if (Number(rai) + Number(ngan) + Number(wah) <= 0)
      return err('land', 'กรุณาระบุขนาดที่ดิน (ไร่ / งาน / วา)');
  }

  if (type_post.includes('ขาย') && parsePrice(price) <= 0)
    return err('price', 'กรุณาระบุราคาขาย');
  if (type_post.includes('เช่า') && parsePrice(rent_price) <= 0)
    return err('rent_price', 'กรุณาระบุราคาเช่า');
  if (config.hasSellOut && type_post.includes('เซ้ง') && parsePrice(sellout_price) <= 0)
    return err('sellout_price', 'กรุณาระบุราคาเซ้ง');

  return ok();
}

export function validatePage3(upload, config) {
  const { owner_status, first_hand, furniture_included, phone_number } = upload;

  if (!owner_status)
    return err('owner_status', 'กรุณาระบุสถานะผู้ขาย (เจ้าของ / นายหน้า)');
  if (config.hasFirstHand && first_hand === null)
    return err('first_hand', 'กรุณาระบุสถานะทรัพย์ (มือหนึ่ง / มือสอง)');
  if (config.hasFurniture && furniture_included === null)
    return err('furniture_included', 'กรุณาระบุสถานะเฟอร์นิเจอร์');
  if (!phone_number?.trim())
    return err('phone_number', 'กรุณาระบุเบอร์โทรศัพท์');
  if (!PHONE_RE.test(phone_number))
    return err('phone_number', 'เบอร์โทร 10 หลัก ขึ้นต้นด้วย 0 (เช่น 0812345678)');

  return ok();
}

export function validatePage4(upload) {
  const { asset_location_latitude, asset_location_longitude } = upload;
  if (asset_location_latitude === null || asset_location_longitude === null)
    return err('location', 'กรุณาปักหมุดตำแหน่งทรัพย์บนแผนที่');
  return ok();
}
